import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Preloader from "@/components/Preloader";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Projects from "@/pages/Projects";
import Turnkey from "@/pages/Turnkey";
import ProjectDetail from "@/pages/ProjectDetail";
import Services from "@/pages/Services";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { MessageCircle, Phone } from "lucide-react";

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/turnkey" element={<Turnkey />} />
      <Route path="/projects" element={<Projects />} />
      <Route path="/projects/:id" element={<ProjectDetail />} />
      <Route path="/services" element={<Services />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/Admin" element={<Admin />} />
    </Routes>
  );
};

const MobileCtaBar = () => {
  const c = useContent();
  const phone = (c.contact.phone || "").replace(/[^+\d]/g, "");
  return (
    <div data-testid="mobile-cta-bar" className="fixed bottom-0 inset-x-0 z-[80] flex md:hidden border-t border-bronze/30 bg-obsidian/95 backdrop-blur">
      <a href={`tel:${phone}`} data-testid="mobile-call-btn" className="flex-1 flex items-center justify-center gap-2 py-4 text-[11px] uppercase tracking-[0.2em] text-bone border-r border-white/10">
        <Phone size={15} className="text-bronze" /> Call Now
      </a>
      <a href={c.socials?.whatsapp} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-btn" className="flex-1 flex items-center justify-center gap-2 py-4 text-[11px] uppercase tracking-[0.2em] text-obsidian bg-bronze">
        <MessageCircle size={15} /> WhatsApp Us
      </a>
    </div>
  );
};

const WhatsAppFloat = () => {
  const c = useContent();
  if (!c.socials?.whatsapp) return null;
  return (
    <a
      href={c.socials.whatsapp}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-float"
      aria-label="Chat on WhatsApp"
      className="hidden md:flex fixed bottom-6 left-6 z-[80] w-12 h-12 border border-bronze/50 bg-obsidian/80 backdrop-blur items-center justify-center text-bronze transition-all duration-500 hover:bg-bronze hover:text-obsidian"
    >
      <MessageCircle size={18} />
    </a>
  );
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <div className="grain-overlay" />
      <BrowserRouter>
        <ContentProvider>
          <Preloader />
          <ScrollManager />
          <Navbar />
          <AnimatedRoutes />
          <Footer />
          <WhatsAppFloat />
          <MobileCtaBar />
        </ContentProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
